import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getApiBase, setApiBase, clearApiBase, hasOverride, isValidBase, normaliseBase, reachableFromHere,
} from '../utils/serverConfig';

const PROBE_TIMEOUT_MS = 6000;

/**
 * Is a PITRIOT API answering at `base`?
 *
 * GET /auth/me with no token. Every PITRIOT API -- the cloud Lambda and the
 * desktop server alike -- answers that with 401 and a JSON body carrying
 * `error`, because the route sits behind token verification. A timeout, an
 * HTML page, or a router's own login screen is not a PITRIOT server, and
 * saving it would leave the app failing every request with no explanation.
 */
export async function probeServer(base) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS) : null;
  try {
    const res = await fetch(`${normaliseBase(base)}/auth/me`,
      controller ? { signal: controller.signal } : undefined);
    if (res.status !== 401 && res.status !== 200) return false;
    const body = await res.json().catch(() => null);
    return Boolean(body && (body.error || body.user_id));
  } catch (e) {
    return false;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** An address an operator may type: absolute http(s), never a bare path. */
export function isUserEnterableBase(value) {
  const base = normaliseBase(value);
  return isValidBase(base) && !base.startsWith('/');
}

/**
 * Choose which server this client talks to.
 *
 * `forced` is set when there is no usable server at all -- a native build on
 * first launch -- in which case the dialog cannot be dismissed without one.
 */
export default function ServerSettings({ onClose, forced = false }) {
  const { t } = useTranslation('account');
  const [value, setValue] = useState(hasOverride() ? getApiBase() : '');
  const [status, setStatus] = useState(null); // null | testing | ok | unreachable | invalid | loopback

  // 127.0.0.1 or localhost typed on a phone names the phone itself. getApiBase
  // would ignore it anyway; refusing here says why instead of saving a choice
  // that silently does nothing.
  const refusal = () => {
    if (!isUserEnterableBase(value)) return 'invalid';
    if (!reachableFromHere(value)) return 'loopback';
    return null;
  };

  const test = async () => {
    const refused = refusal();
    if (refused) { setStatus(refused); return; }
    setStatus('testing');
    setStatus((await probeServer(value)) ? 'ok' : 'unreachable');
  };

  const save = () => {
    const refused = refusal();
    if (refused) { setStatus(refused); return; }
    if (!setApiBase(value)) { setStatus('invalid'); return; }
    // Saved even when the last test failed: a field laptop can be switched off
    // right now and still be the right server for tomorrow.
    onClose?.(true);
  };

  const reset = () => { clearApiBase(); onClose?.(true); };

  const STATUS_TEXT = {
    testing: t('serverTesting'), ok: t('serverOk'),
    unreachable: t('serverUnreachable'), invalid: t('serverInvalid'), loopback: t('serverLoopback'),
  };
  const STATUS_COLOR = { testing: 'var(--c-text-muted)', ok: 'var(--c-success)', unreachable: 'var(--c-warning-text-2)',
                         invalid: 'var(--c-danger-2)', loopback: 'var(--c-danger-2)' };

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="server-settings-title" style={styles.overlay}>
      <div style={styles.card}>
        <h2 id="server-settings-title" style={styles.title}>{t('serverTitle')}</h2>
        {forced && <p style={styles.caution}>{t('serverRequired')}</p>}
        <p style={styles.muted}>
          {t('serverCurrent')}: <code>{getApiBase()}</code>
          {!hasOverride() && ` (${t('serverDefault')})`}
        </p>
        <label style={styles.label} htmlFor="server-address">{t('serverLabel')}</label>
        <input id="server-address" style={styles.input} value={value} dir="ltr"
               inputMode="url" autoCapitalize="none" autoCorrect="off" spellCheck={false}
               placeholder="https://" title={t('tip.serverAddress')} onChange={e => { setValue(e.target.value); setStatus(null); }} />
        <p style={styles.hint}>{t('serverHint')}</p>
        {status && <p data-testid="server-status" style={{ ...styles.status, color: STATUS_COLOR[status] }}>{STATUS_TEXT[status]}</p>}
        <div style={styles.row}>
          <button type="button" style={styles.secondary} onClick={test} disabled={status === 'testing'} title={t('tip.serverTest')}>
            {t('serverTest')}
          </button>
          <button type="button" style={styles.primary} onClick={save} title={t('tip.serverSave')}>{t('serverSave')}</button>
        </div>
        <div style={styles.row}>
          {hasOverride() && (
            <button type="button" style={styles.link} onClick={reset} title={t('tip.serverReset')}>{t('serverReset')}</button>
          )}
          {!forced && (
            <button type="button" style={styles.link} onClick={() => onClose?.(false)} title={t('tip.serverCancel')}>{t('cancel')}</button>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.45)', display: 'flex',
             alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 },
  card: { background: 'var(--c-surface)', borderRadius: 12, padding: 24, width: '100%', maxWidth: 440,
          boxShadow: '0 10px 30px rgba(0,0,0,0.2)' },
  title: { margin: '0 0 8px', fontSize: 18 },
  muted: { fontSize: 13, color: 'var(--c-text-body-3-fg)', wordBreak: 'break-all' },
  caution: { fontSize: 13, color: 'var(--c-warning-text-fg)', background: 'var(--c-warning-soft-2)', border: '1px solid var(--c-warning)',
             borderRadius: 6, padding: '8px 10px' },
  label: { display: 'block', fontSize: 13, fontWeight: 600, marginTop: 12, marginBottom: 4 },
  input: { width: '100%', boxSizing: 'border-box', fontSize: 16, padding: '9px 10px',
           border: '1px solid var(--c-border-strong)', borderRadius: 6 },
  hint: { fontSize: 12, color: 'var(--c-text-muted-fg)', margin: '6px 0 0' },
  status: { fontSize: 13, fontWeight: 600, margin: '10px 0 0' },
  row: { display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end', flexWrap: 'wrap' },
  primary: { background: 'var(--c-primary)', color: 'var(--c-on-accent)', border: 'none', borderRadius: 6,
             padding: '9px 16px', fontWeight: 600, cursor: 'pointer' },
  secondary: { background: 'var(--c-surface)', color: 'var(--c-primary-fg)', border: '1px solid var(--c-primary)', borderRadius: 6,
               padding: '9px 16px', fontWeight: 600, cursor: 'pointer' },
  link: { background: 'none', border: 'none', color: 'var(--c-info-fg)', cursor: 'pointer', padding: 4, fontSize: 13 },
};
